import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Building2, Layers, Plus, MapPin, ExternalLink } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import {
  useHoaTree,
  type HoaDeveloperNode,
  type HoaCommunityNode,
} from '@/admin/hooks/use-hoa-communities'

/**
 * HOA hierarchy for a single Network partner (HOA / Developer role).
 *
 * Read-display of the partner's communities → buildings → units in the
 * Product-Library column style. Full add/bulk management lives on the HOA
 * Communities workspace; this surfaces the structure where the partner lives.
 */
export function HoaPartnerCommunities({
  partnershipUuid,
}: {
  partnershipUuid: string
}) {
  const navigate = useNavigate()
  const { data: tree = [], isLoading } = useHoaTree()

  const communities = useMemo<HoaCommunityNode[]>(() => {
    const node = tree.find(
      (n): n is HoaDeveloperNode =>
        n.type === 'developer' && n.uuid === partnershipUuid,
    )
    return node?.communities ?? []
  }, [tree, partnershipUuid])

  return (
    <div className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-8">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Communities &amp; Units
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(`/hoa-communities/new?developer=${partnershipUuid}`)
            }
            className="cursor-pointer gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]"
          >
            <Plus size={12} />
            Community
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/hoa-communities')}
            className="cursor-pointer gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <ExternalLink size={12} />
            Manage
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="py-6 text-[12px] text-[var(--ink-3)]">Loading…</p>
      ) : communities.length === 0 ? (
        <p className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          No communities registered to this partner yet. Use “+ Community” to add
          one.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {communities.map((c) => (
            <div key={c.uuid}>
              <div className="mb-1.5 flex items-center gap-2">
                <Building2 size={15} className="text-[var(--green)]" />
                <span className="text-[14px] text-[var(--ink)]">{c.name}</span>
                {c.address && (
                  <span className="flex items-center gap-1 text-[11px] text-[var(--ink-3)]">
                    <MapPin size={11} />
                    {c.address}
                  </span>
                )}
              </div>
              {c.buildings.map((b) => (
                <div
                  key={b.uuid}
                  className="ml-5 mt-2 border-l-[0.5px] border-[var(--rule)] pl-4"
                >
                  <div className="flex items-center gap-2 py-1">
                    <Layers size={14} className="text-[var(--ink-2)]" />
                    <span className="text-[13px] font-medium text-[var(--ink)]">
                      {b.name}
                    </span>
                    <span className="text-[11px] text-[var(--ink-3)]">
                      {b.units.length} unit{b.units.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {b.units.length > 0 && (
                    <div className="mt-1 overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
                      <div className="grid grid-cols-[1.4fr_1.6fr_1fr_1.4fr] gap-2 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
                        <span>Unit</span>
                        <span>Product</span>
                        <span>Colour</span>
                        <span>Contractor</span>
                      </div>
                      {b.units.map((u) => (
                        <div
                          key={u.uuid}
                          className="grid grid-cols-[1.4fr_1.6fr_1fr_1.4fr] gap-2 border-b-[0.5px] border-[var(--rule)] px-3 py-2 text-[12px] text-[var(--ink)] last:border-b-0"
                        >
                          <span>{u.name}</span>
                          <span className="text-[var(--ink-2)]">
                            {u.product || '—'}
                          </span>
                          <span className="text-[var(--ink-2)]">
                            {u.color || '—'}
                          </span>
                          <span className="text-[var(--ink-2)]">
                            {u.contractor || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
